import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { UseQueryOptions } from '@tanstack/react-query'
import { Address } from 'viem'
import { Config as WagmiConfig } from 'wagmi'

import { SUPPORTED_CHAIN_IDS } from './constants'
import { TokenDefinitionRepository } from '@/domain/token-repository/TokenRepository'
import { PriceConfig } from '@/domain/token-repository/types'
import { Token } from '@/domain/types/Token'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { SavingsRateChartData } from '@/features/savings/data/savings-charts/savings-rate-query/types'
import {
  SavingsConverterQueryOptions,
  SavingsConverterQueryParams,
} from '@/features/savings/logic/savings-converters/types'
import {
  OracleInfoFetcherParams,
  OracleInfoFetcherResult,
} from '@/features/sparklend/common/data/oracles/oracleInfoFetchers'
import { MarketRateChartData } from '@/features/sparklend/market-details/data/market-charts/types'

export type SupportedChainId = (typeof SUPPORTED_CHAIN_IDS)[number]

export type SavingsRateQueryOptions = (args: {
  savingsToken: Token
  chainId: number
  historyCutoff?: Date
}) => UseQueryOptions<any, Error, SavingsRateChartData, any>
export interface SavingsAccountStats {
  tvl: NormalizedNumber
  users: number
  liquidity?: NormalizedNumber
  depositCap?: NormalizedNumber
  systemAvailableLiquidity?: NormalizedNumber
}
export type SavingsAccountStatsQueryOptions = (args: {
  wagmiConfig: WagmiConfig
  chainId: number
}) => UseQueryOptions<any, Error, SavingsAccountStats, any>

export type SavingsBackedByData = {
  category:
    | 'aaa_corporate_debt'
    | 'basis_trade'
    | 'onchain_crypto_lending'
    | 'otc_crypto_lending'
    | 'short_duration_treasury_bills'
    | 'stablecoins'
    | 'private_credit'
    | 'basis_trade_cme'
    | 'idle'
    | 'sparklend_eth'
    | 'other'
  amount: NormalizedNumber
}[]
export interface BackedByQueryResult {
  queryOptions: UseQueryOptions<any, Error, SavingsBackedByData, any>
  groupUsdsDetails: boolean
}
export type BackedByQueryOptions = (args: { chainId: number; wagmiConfig: WagmiConfig }) => BackedByQueryResult

export type SavingsLiquidityData = {
  protocol: string
  amount: NormalizedNumber
}[]

export interface LiquidityChartQueryResult {
  queryOptions: UseQueryOptions<any, Error, SavingsLiquidityData, any>
}
export type LiquidityChartQueryOptions = () => LiquidityChartQueryResult

export type BorrowRateQueryOptions = (symbol: TokenSymbol) => UseQueryOptions<any, Error, MarketRateChartData, any>
export type SupplyRateQueryOptions = (symbol: TokenSymbol) => UseQueryOptions<any, Error, MarketRateChartData, any>

export interface NativeAssetInfo {
  nativeAssetName: string
  nativeAssetSymbol: TokenSymbol
  wrappedNativeAssetSymbol: TokenSymbol
  wrappedNativeAssetAddress: CheckedAddress
  minRemainingNativeAssetBalance: NormalizedNumber
  nativeAssetLogo: string
}

export interface ChainMeta {
  name: string
  logo: string
}

export type PermitSupport = Record<CheckedAddress, boolean>

export type TokensWithMalformedApprove = CheckedAddress[]

export type TokensRequiringResetApproval = CheckedAddress[]

export type contractsRequiringUsdtApprovalReset = Address[]

export type TokenSymbolToNameReplacement = Record<TokenSymbol, { name: string; symbol: TokenSymbol }>

export interface TokenToAirdropAmounts {
  [token: TokenSymbol]: {
    deposit?: NormalizedNumber
    borrow?: NormalizedNumber
  }
}

export interface AirdropsPerAction {
  deposit: TokenSymbol[]
  borrow: TokenSymbol[]
}
export type Airdrop = Record<TokenSymbol, AirdropsPerAction>

export type OracleFeedProvider = 'chainlink' | 'chronicle' | 'redstone'

export type ReserveOracleType =
  | { type: 'market-price'; providedBy: OracleFeedProvider[] }
  | {
      type: 'yielding-fixed'
      baseAssetSymbol: TokenSymbol
      providedBy: OracleFeedProvider[]
      oracleFetcher: (params: OracleInfoFetcherParams) => Promise<OracleInfoFetcherResult>
    }
  | { type: 'fixed' }
  | { type: 'underlying-asset'; asset: string; providedBy?: OracleFeedProvider[] }

export type SavingsConverterQuery = (args: SavingsConverterQueryParams) => SavingsConverterQueryOptions

export interface SparkLendConfig {
  defaultAssetToBorrow: TokenSymbol
  easyBorrow: boolean
  easyBorrowTokensToBorrow: TokenSymbol[]
  sllManagedMarkets: TokenSymbol[]
  supplyRateQueryOptions: SupplyRateQueryOptions | undefined
  borrowRateQueryOptions: BorrowRateQueryOptions | undefined
  oracles: Record<TokenSymbol, ReserveOracleType>
}

export interface AccountConfig {
  supportedAssets: TokenSymbol[]
  savingsToken: TokenSymbol
  underlyingToken: TokenSymbol
  fetchConverterQuery: (args: SavingsConverterQueryParams) => SavingsConverterQueryOptions
  savingsRateQueryOptions: SavingsRateQueryOptions | undefined
  stats:
    | {
        queryOptions: SavingsAccountStatsQueryOptions | undefined
        items: SavingsStatsItem[]
      }
    | undefined
  backedByQueryOptions: BackedByQueryOptions | undefined
  liquidityChartQueryOptions: LiquidityChartQueryOptions | undefined
  sparkPointsMultiplier: number | undefined
  historyCutoff?: Date
}

export type SavingsStatsItem =
  | 'tvl'
  | 'users'
  | 'deposit-cap'
  | 'liquidity-sky-psm'
  | 'liquidity-spark-psm'
  | 'liquidity-stusds'
  | 'liquidity-spark-vaults-v2'

export interface SavingsConfig {
  priceConfigs: Record<TokenSymbol, PriceConfig>
  accounts: AccountConfig[]
  psmStables: TokenSymbol[] | undefined
}

export type AssetsGroup =
  | {
      type: 'stablecoins'
      name: 'Stablecoins'
      assets: TokenSymbol[]
    }
  | {
      type: 'governance'
      name: 'Governance Tokens'
      assets: TokenSymbol[]
    }

export type FarmConfig = {
  address: CheckedAddress
  entryAssetsGroup: AssetsGroup
  historyCutoff?: Date
  getFarmDetailsApiUrl?: (address: CheckedAddress, page?: number) => string
} & (
  | {
      rewardType: 'token'
      rewardToken: TokenSymbol
    }
  | {
      rewardType: 'points'
      rewardPoints: TokenDefinition
    }
)

export interface LegacyFarmsConfig {
  priceConfigs: Record<TokenSymbol, PriceConfig>
  getFarmDetailsApiUrl: (address: CheckedAddress, page?: number) => string
  configs: FarmConfig[]
}

export interface SpkConfig {
  priceConfigs: Record<TokenSymbol, PriceConfig>
  farmConfig: FarmConfig
}

export interface PointsConfig {
  priceConfigs: Record<TokenSymbol, PriceConfig>
}

export type SparkRewardsConfig = {}

export interface SwapConfig {
  priceConfigs: Record<TokenSymbol, PriceConfig>
}

export interface ChainConfigEntry {
  originChainId: SupportedChainId
  meta: ChainMeta
  tokenDefinitionRepository: TokenDefinitionRepository
  nativeAssetInfo: NativeAssetInfo
  permitSupport: PermitSupport
  tokensWithMalformedApprove: TokensWithMalformedApprove
  tokensRequiringResetApproval: TokensRequiringResetApproval
  contractsRequiringUsdtResetApproval: contractsRequiringUsdtApprovalReset
  sparkLend: SparkLendConfig | undefined
  savings: SavingsConfig | undefined
  legacyFarms: LegacyFarmsConfig | undefined
  spk: SpkConfig | undefined
  points: PointsConfig | undefined
  sparkRewards: SparkRewardsConfig | undefined
  swap: SwapConfig | undefined
}

export type ChainConfig = Record<SupportedChainId, ChainConfigEntry>

export const CHRONICLE_ORACLE_PRECISION = 18

// @note: zero-price oracle is used for tokens for which we don't have a price feed yet
export type OracleType = 'fixed-usd' | 'vault' | 'zero-price' | 'ssr-auth-oracle' | 'chronicle'

export type TokenConfig = { symbol: TokenSymbol; address: CheckedAddress } & (
  | {
      oracleType: Extract<OracleType, 'fixed-usd' | 'zero-price' | 'ssr-auth-oracle'>
    }
  | {
      oracleType: 'vault'
      sharesDecimals?: number
      assetsDecimals?: number
    }
  | {
      oracleType: 'chronicle'
      oracleAddress: Address
    }
)
